import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
} from "react";
import { getUser } from "@/api/auth";
import { UserI } from "@/types/auth";
import ContainerLoader from "@/ui/ContainerLoader/ContainerLoader";
import { useAuthStore } from "@/store/auth";
import { useNavigate } from "react-router-dom";
import { showNotification } from "@/utils/network";
import { useTranslation } from "react-i18next";
import { Answer, Surveys } from "@/types/surveys";

interface UserDataContextProps {
  userData: UserI | null;
  setLight: (value: number) => void;

  chatMessageLoading: boolean;
  setChatMessageLoading: (value: boolean) => void;

  updateSurveyAnswers: (
    petId: string,
    surveyId: number,
    answersList: Answer[],
    survey: Surveys
  ) => void;

  deleteSurvey: (petId: string, surveyId: number) => void;
}

export const UserDataContext = createContext<UserDataContextProps | undefined>(
  undefined
);

interface UserDataProviderProps {
  children: ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const [userData, setUserData] = useState<UserI | null>(null);
  const [loading, setLoading] = useState(true);

  const [chatMessageLoading, setChatMessageLoading] = useState<boolean>(false);

  const setLight = (value: number) => {
    setUserData((p) => {
      if (!p) return null;
      return {
        ...p,
        light: {
          ...p.light,
          energy_count: value,
        },
      };
    });
  };

  const updateSurveyAnswers = (
    petId: string,
    surveyId: number,
    answersList: Answer[],
    survey: Surveys
  ) => {
    setUserData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pets: prev.pets.map((pet) => {
          if (pet.id !== petId) return pet;

          // Проверяем, есть ли уже такая анкета у питомца
          const existingSurveyIndex = pet.surveys.findIndex(
            (survey) => survey.id === surveyId
          );

          // Если анкета существует - обновляем её
          if (existingSurveyIndex !== -1) {
            return {
              ...pet,
              surveys: pet.surveys.map((survey) => {
                if (survey.id !== surveyId) return survey;

                return {
                  ...survey,
                  questions: survey.questions.map((question) => {
                    const answer = answersList.find(
                      (a) => a.question_id === question.id
                    );
                    return {
                      ...question,
                      answer: answer?.text || null,
                    };
                  }),
                };
              }),
            };
          }

          // Если анкеты нет - создаём новую
          const filterAnswers = answersList.some(
            (answer) =>
              answer.text &&
              answer.text !== "" &&
              answer.text !== "WITHOUT_RESPONSE"
          );

          const newSurvey = {
            id: surveyId,
            title: survey.title,
            description: "",
            questions: answersList.map((answer) => {
              // Находим оригинальный вопрос из survey.questions
              const originalQuestion = survey.questions.find(
                (q) => q.id === answer.question_id
              );

              return {
                id: answer.question_id,
                text: originalQuestion?.text || "",
                order: originalQuestion?.order ?? 0,
                answer: answer.text,
              };
            }),
            response_id: survey.response_id,
          };

          return {
            ...pet,
            surveys: [...pet.surveys, newSurvey],
          };
        }),
      };
    });
  };

  const deleteSurvey = (petId: string, surveyId: number) => {
    setUserData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        pets: prev.pets.map((pet) => {
          if (pet.id !== petId) return pet;

          return {
            ...pet,
            surveys: pet.surveys.filter((survey) => survey.id !== surveyId),
          };
        }),
      };
    });
  };

  const loadUser = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setUserData(null);
      return;
    }

    setLoading(true);

    try {
      const res = await getUser();
      setUserData(res);
    } catch (e) {
      clearAuth();
      showNotification({
        text: t("errors.generic"),
        type: "error",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [isAuthenticated]);

  if (loading) return <ContainerLoader />;

  return (
    <UserDataContext.Provider
      value={{
        userData,
        setLight,
        chatMessageLoading,
        setChatMessageLoading,
        updateSurveyAnswers,
        deleteSurvey,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserDataContext = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error(
      "useUserDataContext must be used within a UserDataProvider"
    );
  }
  return context;
};
